import React, { Fragment } from 'react'

import { ProcessEntity, ProcessStatus } from '../Types/entities'
import { TableColumn } from '../../../components/_EOS/GenericTable'
import { Chip, Tooltip, Typography } from '@mui/material'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import ScheduleIcon from '@mui/icons-material/Schedule'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { utcToLocal, configDateInitial } from '../../../utils/dateHelper'
import { Filter } from '../../../components/_WMS/FilterComponent'

const formatDate = (value: unknown) =>
  value ? utcToLocal(value.toString(), 'dd/MM/yyyy HH:mm:ss') : ''

// Rango de fechas por defecto al cargar la página: últimos 7 días
export const getDefaultDateRangeFilters = (): Filter[] => {
  const initialDate = configDateInitial(7)
  const finalDate = configDateInitial(0)

  return [
    {
      key: 'InitialDate',
      label: 'Fecha Desde',
      value: initialDate,
      displayValue: initialDate
    },
    {
      key: 'FinalDate',
      label: 'Fecha Hasta',
      value: finalDate,
      displayValue: finalDate
    }
  ]
}

export const getColumnsConfig = (): TableColumn<ProcessEntity>[] => [
  { key: 'processId', title: 'Nro.' },
  { key: 'typeProcessName', title: 'Tipo' },
  {
    key: 'fileName',
    title: 'Nombre',
    render: (value) => formatFileName(value as string)
  },
  { key: 'userName', title: 'Usuario' },
  {
    key: 'statusId',
    title: 'Estado',
    render: (value) => setStatus(value)
  },
  {
    key: 'createDate',
    title: 'Fecha Creación',
    render: (value) => formatDate(value)
  },
  {
    key: 'startDate',
    title: 'Fecha Inicio',
    render: (value) => formatDate(value)
  },
  {
    key: 'endDate',
    title: 'Fecha Fin',
    render: (value) => formatDate(value)
  }
]

type StatusColor = 'info' | 'warning' | 'success' | 'error'


const setStatus = (id: number | unknown) => {
  let label = ''
  let icon = null
  let color: StatusColor = 'error'

  switch (id) {
    case ProcessStatus.Created:
      label = 'Creado'
      icon = <PlaylistAddCheckIcon />
      color = 'info'
      break
    case ProcessStatus.InProcess:
      label = 'En Proceso'
      icon = <ScheduleIcon />
      color = 'warning'
      break
    case ProcessStatus.Finalized:
      label = 'Finalizado'
      icon = <CheckCircleOutlineIcon />
      color = 'success'
      break
    default:
      label = 'Con Errores'
      icon = <ErrorOutlineIcon />
      color = 'error'
      break
  }

  return (
    <Fragment>
      <Chip
        icon={icon}
        label={label}
        size='small'
        sx={(theme) => ({
          backgroundColor: theme.palette[color].main,
          color: theme.palette[color].contrastText,
          // Ancho comun para los cuatro estados (lo fija el label mas largo,
          // "Con Errores") con el contenido centrado: la columna queda como una
          // sola pila de pastillas y no un escalonado de anchos distintos.
          minWidth: 100,
          justifyContent: 'center',
          '& .MuiChip-label': {
            fontSize: '0.8rem',
            fontWeight: 600,
            px: 0.5
          },
          '& .MuiChip-icon': {
            fontSize: '0.9rem',
            mr: 0.1,
            // El icono de Chip no hereda el color del texto: trae el suyo.
            color: 'inherit'
          }
        })}
      />
    </Fragment>
  )
}


export const downloadBase64File = (
  base64: string,
  fileName: string,
  mimeType: string
) => {
  // Decodifica la cadena base64
  const byteCharacters = atob(base64)
  const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0))
  const byteArray = new Uint8Array(byteNumbers)

  // Crea un Blob con el tipo MIME correcto
  const blob = new Blob([byteArray], { type: mimeType })

  // Crea un enlace y lo dispara para descargar
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()

  // Limpieza
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const formatFileName = (fileName: string) => {
  return (
    <Tooltip title={fileName}>
      <Typography
        variant='body2'
        sx={{
          // El nombre envuelve hasta 2 líneas cuando la columna se angosta y,
          // si aún no entra, corta con elipsis (...).
          maxWidth: 192,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {fileName}
      </Typography>
    </Tooltip>
  )
}

export const downloadBase64FileDialog = (
  base64: string,
  fileName: string,
  mimeType: string
) => {
  // Decodifica la cadena base64
  const byteCharacters = atob(base64)
  const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0))
  const byteArray = new Uint8Array(byteNumbers)

  // Crea un Blob con el tipo MIME correcto
  const blob = new Blob([byteArray], { type: mimeType })

  // Crea un enlace y lo dispara para descargar
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()

  // Limpieza
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
